import { useEffect, useState } from "react";
import { useLang } from "../context/LangContext";
import { formatApiError } from "../lib/api";
import { supabase } from "../lib/supabase";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Trash2, Plus, Users, Apple, Activity, FileText, Pencil } from "lucide-react";

const empty = { name: "", serving_size: "", category: "", potassium_mg: 0, phosphorus_mg: 0, sodium_mg: 0, protein_g: 0 };
const limits = { potassium: 3000, phosphorus: 1000, sodium: 2000, protein: 60, water: 1500 };
const logDate = (log) => (log.logged_at || new Date().toISOString()).slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);
const emptyTotals = () => ({ potassium: 0, phosphorus: 0, sodium: 0, protein: 0 });
const mapEntry = (log) => {
  return {
    potassium: Math.round(Number(log.potassium_mg || 0)),
    phosphorus: Math.round(Number(log.phosphorus_mg || 0)),
    sodium: Math.round(Number(log.sodium_mg || 0)),
    protein: Math.round(Number(log.protein_g || 0)),
  };
};

export default function Admin() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [foods, setFoods] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [report, setReport] = useState(null);

  const load = async () => {
    try {
      const [usersRes, foodsRes, logsRes] = await Promise.all([
        supabase.from("profiles").select("*"),
        supabase.from("foods").select("*"),
        supabase
          .from("food_logs")
          .select("id, user_id, quantity, potassium_mg, phosphorus_mg, sodium_mg, protein_g, logged_at"),
      ]);
      const error = usersRes.error || foodsRes.error || logsRes.error;
      if (error) throw error;
      const logs = logsRes.data || [];
      setStats({
        users: (usersRes.data || []).length,
        foods: (foodsRes.data || []).length,
        total_logs: logs.length,
        logs_today: logs.filter((log) => logDate(log) === today()).length,
        active_users_today: new Set(logs.filter((log) => logDate(log) === today()).map((log) => log.user_id)).size,
      }); setUsers(usersRes.data || []); setFoods(foodsRes.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const saveFood = async () => {
    try {
      const payload = {
        ...form,
        potassium_mg: Number(form.potassium_mg),
        phosphorus_mg: Number(form.phosphorus_mg),
        sodium_mg: Number(form.sodium_mg),
        protein_g: Number(form.protein_g),
      };
      const { error } = editing?.id
        ? await supabase.from("foods").update(payload).eq("id", editing.id)
        : await supabase.from("foods").insert(payload);
      if (error) throw error;
      toast.success("Saved"); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteFood = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    const { error } = await supabase.from("foods").delete().eq("id", id);
    if (error) return toast.error(formatApiError(error));
    toast.success("Deleted"); load();
  };

  const deleteUser = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      const { error } = await supabase.from("profiles").delete().eq("id", id);
      if (error) throw error;
      toast.success("Deleted"); load();
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openFoodDialog = (food = null) => {
    setEditing(food); setForm(food || empty);
  };

  const loadReport = async (uid) => {
    try {
      const [userRes, logsRes] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
        supabase
          .from("food_logs")
          .select("id, user_id, quantity, potassium_mg, phosphorus_mg, sodium_mg, protein_g, logged_at")
          .eq("user_id", uid),
      ]);
      const error = userRes.error || logsRes.error;
      if (error) throw error;
      const waterByDate = {};
      const grouped = (logsRes.data || []).reduce((acc, log) => {
        const date = logDate(log);
        if (!acc[date]) acc[date] = { date, totals: emptyTotals(), limits, entries: [], water_ml: 0 };
        const entry = mapEntry(log);
        acc[date].entries.push(entry);
        acc[date].totals.potassium += entry.potassium;
        acc[date].totals.phosphorus += entry.phosphorus;
        acc[date].totals.sodium += entry.sodium;
        acc[date].totals.protein += entry.protein;
        acc[date].water_ml = waterByDate[date] || 0;
        return acc;
      }, {});
      setReport({ user: userRes.data, days: Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date)).slice(-14) });
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = () => {
    if (!report) return;
    const rows = [["Date", "Potassium", "Phosphorus", "Sodium", "Protein", "Water(ml)"]];
    report.days.forEach((d) => rows.push([d.date, d.totals.potassium, d.totals.phosphorus, d.totals.sodium, d.totals.protein, d.water_ml]));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `report-${report.user.email}.csv`; a.click();
  };

  if (!stats) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>{t("admin_title")}</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat icon={Users} label={t("total_users")} value={stats.users} color="var(--rs-primary)" />
        <Stat icon={Apple} label={t("total_foods")} value={stats.foods} color="var(--rs-sodium)" />
        <Stat icon={FileText} label={t("total_logs")} value={stats.total_logs} color="var(--rs-phosphorus)" />
        <Stat icon={Activity} label={t("logs_today")} value={stats.logs_today} color="var(--rs-potassium)" />
        <Stat icon={Users} label={t("active_today")} value={stats.active_users_today} color="var(--rs-protein)" />
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users">{t("manage_users")}</TabsTrigger>
          <TabsTrigger value="foods" data-testid="tab-foods">{t("manage_foods")}</TabsTrigger>
          {report && <TabsTrigger value="report" data-testid="tab-report">{t("view_report")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="users">
          <div className="bento-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-start border-b border-border">
                    <th className="py-3 text-start">Name</th>
                    <th className="py-3 text-start">Email</th>
                    <th className="py-3 text-start">Role</th>
                    <th className="py-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-border/50" data-testid={`user-${u.id}`}>
                      <td className="py-3">{u.name}</td>
                      <td className="py-3 text-muted-foreground">{u.email}</td>
                      <td className="py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${u.role === "admin" ? "bg-primary/10 text-primary" : "bg-muted"}`}>{u.role}</span>
                      </td>
                      <td className="py-3 text-end">
                        <Button size="sm" variant="outline" onClick={() => loadReport(u.id)} className="me-2" data-testid={`report-${u.id}`}>{t("view_report")}</Button>
                        {u.role !== "admin" && (
                          <Button size="sm" variant="ghost" onClick={() => deleteUser(u.id)} className="text-destructive" data-testid={`del-user-${u.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="foods">
          <div className="flex justify-end mb-3">
            <Dialog open={!!editing || editing === null && false} onOpenChange={(o) => { if (!o) { setEditing(null); setForm(empty); } }}>
              <DialogTrigger asChild>
                <Button className="btn-primary gap-2" onClick={() => openFoodDialog({ ...empty })} data-testid="new-food-btn">
                  <Plus className="w-4 h-4" /> {t("add_new_food")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>{editing?.id ? t("edit") : t("add_new_food")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ["name", "Name"],
                    ["serving_size", "Serving"], ["category", "Category"],
                    ["potassium_mg", "Potassium (mg)"], ["phosphorus_mg", "Phosphorus (mg)"],
                    ["sodium_mg", "Sodium (mg)"], ["protein_g", "Protein (g)"],
                  ].map(([k, label]) => (
                    <div key={k} className="col-span-1">
                      <Label>{label}</Label>
                      <Input
                        value={form[k]}
                        onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                        type={["potassium_mg", "phosphorus_mg", "sodium_mg", "protein_g"].includes(k) ? "number" : "text"}
                        className="h-11 mt-1"
                        data-testid={`food-field-${k}`}
                      />
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setEditing(null); setForm(empty); }}>{t("cancel")}</Button>
                  <Button className="btn-primary" onClick={saveFood} data-testid="save-food-btn">{t("save")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bento-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-3 text-start">Name</th>
                    <th className="py-3 text-start">Serving</th>
                    <th className="py-3 text-start">Category</th>
                    <th className="py-3 text-start">K/P/Na/Pr</th>
                    <th className="py-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {foods.map((f) => (
                    <tr key={f.id} className="border-b border-border/50">
                      <td className="py-3">{f.name}</td>
                      <td className="py-3 text-muted-foreground">{f.serving_size}</td>
                      <td className="py-3">{f.category}</td>
                      <td className="py-3 text-xs">{f.potassium_mg}/{f.phosphorus_mg}/{f.sodium_mg}/{f.protein_g}</td>
                      <td className="py-3 text-end">
                        <Button size="icon" variant="ghost" onClick={() => openFoodDialog(f)} data-testid={`edit-food-${f.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteFood(f.id)} className="text-destructive" data-testid={`del-food-${f.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {report && (
          <TabsContent value="report">
            <div className="bento-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-lg">{report.user.name}</h3>
                  <p className="text-sm text-muted-foreground">{report.user.email}</p>
                </div>
                <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">Export CSV</Button>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-2 text-start">Date</th>
                    <th className="py-2 text-start">K (mg)</th>
                    <th className="py-2 text-start">P (mg)</th>
                    <th className="py-2 text-start">Na (mg)</th>
                    <th className="py-2 text-start">Protein (g)</th>
                    <th className="py-2 text-start">Water (ml)</th>
                  </tr>
                </thead>
                <tbody>
                  {report.days.map((d) => (
                    <tr key={d.date} className="border-b border-border/50">
                      <td className="py-2">{d.date}</td>
                      <td className="py-2">{d.totals.potassium}</td>
                      <td className="py-2">{d.totals.phosphorus}</td>
                      <td className="py-2">{d.totals.sodium}</td>
                      <td className="py-2">{d.totals.protein}</td>
                      <td className="py-2">{d.water_ml}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bento-card">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}22` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      </div>
      <div className="text-3xl font-extrabold" style={{ fontFamily: "IBM Plex Sans", color }}>{value}</div>
    </div>
  );
}
